import {useEffect, useState} from 'react';
import {useParams} from 'react-router';
import {getBankAccounts, getTransaction, updateTransactionAmount, updateTransactionDate,
    getTransactionLog, updateTransactionNotes, updateTransactionDepartment, updateTransactionCategory,
    updateTransactionVendor,
} from "../../api/Api";
import {BankTransactionType} from "../../api/Constants";
import {TransactionAmountWidget} from "./TransactionAmountWidget";
import {Card, Row, Col, message} from 'antd';
import {DatePickerWidget} from "../widget/DatePickerWidget";
import {TransactionLogTable} from "./TransactionLogTable";
import {TransactionTextWidget} from "./TransactionTextWidget";
import {VendorWidget} from "./VendorWidget";

export const TransactionView = ({session, refresh, onRefresh, onApiFail, props}) => {
    const params = useParams();
    const {transactionId} = params;
    const [messageApi, contextHolder] = message.useMessage();

    const [transaction, setTransaction] = useState({});
    const [bankMap, setBankMap] = useState({});

    const [log, setLog] = useState([]);

    const successMessage = (content) => {
        messageApi.open({
            type: 'success',
            content: content,
        })
    }

    const failMessage = (content) => {
        messageApi.open({
            type: 'error',
            content: content,
        })
    }

    useEffect(() => {
        getTransaction(transactionId).then(r => {
            setTransaction({...r, key: r.id});
            return getTransactionLog(r.id);
        }).then(r => {
            const sortedLog = r.map(row => {
                return {...row, key: row.id}
            }).sort((a, b) => {
                const aDate = new Date(a.timestamp);
                const bDate = new Date(b.timestamp);
                return aDate-bDate;
            });
            setLog(sortedLog);
        }).catch (e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }, [transactionId, onApiFail]);

    useEffect(() => {
        getBankAccounts().then(r => {
            // create a map
            let nextMap = {};
            r.forEach(acc => {
                nextMap[acc.id] = {...acc};
            });
            setBankMap(nextMap);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }, [onApiFail]);

    const onAmountChange = (amount) => {
        updateTransactionAmount(transactionId, amount).then(r => {
            successMessage('Amount Updated');
            if (onRefresh) onRefresh();
        }).catch(e => {
            failMessage('Failed to Update Amount');
        });
    }

    const onDateChange = (dateString, dateJs) => {
        updateTransactionDate(transactionId, dateString).then(r => {
            successMessage('Date Updated');
            if (onRefresh) onRefresh();
        }).catch(e => {
            failMessage('Failed to Update Date');
        });
    }

    const onNotesChange = (newNotes) => {
        updateTransactionNotes(transactionId, newNotes).then(r => {
            successMessage('Notes Updated');
            if (onRefresh) onRefresh();
        }).catch(e => {
            failMessage('Failed to Update Notes');
        });
    }

    const onDepartmentChange = (newDepartment) => {
        updateTransactionDepartment(transactionId, newDepartment).then(r => {
            successMessage('Department Updated');
            if (onRefresh) onRefresh();
        }).catch(e => {
            failMessage('Failed to Update Department');
        });
    }

    const onCategoryChange = (newCategory) => {
        updateTransactionCategory(transactionId, newCategory).then(r => {
            successMessage('Category Updated');
            if (onRefresh) onRefresh();
        }).catch(e => {
            failMessage('Failed to Update Category');
        });
    }

    const onVendorIdChange = (vendorId) => {
        updateTransactionVendor(transactionId, vendorId).then(r => {
            successMessage('Vendor Updated');
            if (onRefresh) onRefresh();
        }).catch(e => {
            failMessage('Failed to Update Vendor');
        });
    }

    const divStyle = {
        textAlign: 'left'
    }

    const rowStyle = {
        marginTop: '5px',
        marginBottom: '5px',
    }

    const labelStyle = {
        marginTop: '5px'
    }

    const {type, amount, accountId, date, notes} = transaction;
    let detailContent = <div />
    let bankName = "";
    if ((accountId !== undefined) && (bankMap[accountId] !== undefined)) {
        bankName = bankMap[accountId].name
    }

    const commonDetails = <div>
        <Row style={rowStyle}>
            <Col span={2}><b>Account:</b></Col>
            <Col>{bankName}</Col>
        </Row>
        <Row style={rowStyle}>
            <Col span={2}><b>Type:</b></Col>
            <Col>{BankTransactionType.Label[type]}</Col>
        </Row>
        <Row style={rowStyle}>
            <Col span={2}><div style={labelStyle}><b>Amount:</b></div></Col>
            <Col>
                <TransactionAmountWidget title={'Change Transaction Amount'} value={amount} min={0} onAmountChange={onAmountChange}/>
            </Col>
        </Row>
        <Row style={rowStyle}>
            <Col span={2}><div style={labelStyle}><b>Date:</b></div></Col>
            <Col>
                <DatePickerWidget date={new Date(date)} onChange={onDateChange} />
            </Col>
        </Row>
        <Row style={rowStyle}>
            <Col span={2}><div style={labelStyle}><b>Notes:</b></div></Col>
            <Col>
                <TransactionTextWidget title={'Update Notes'} placeholder={'Notes'} value={notes} maxLength={512} onTextChange={onNotesChange} />
            </Col>
        </Row>
    </div>

    if ((type === BankTransactionType.Payment) || (type === BankTransactionType.Invoice) || (type === BankTransactionType.Transfer) || (type === BankTransactionType.Return)) {
        detailContent = <div>
            {commonDetails}
        </div>
    } else if (type === BankTransactionType.Expense) {
        const {department, category, company} = transaction;
        detailContent = <div>
            {commonDetails}
            <Row style={rowStyle}>
                <Col span={2}><div style={{marginTop: '5px'}}><b>Department:</b></div></Col>
                <Col>
                    <TransactionTextWidget title={'Update Department'} placeholder={'Department'} value={department} maxRows={1}
                       maxLength={64} onTextChange={onDepartmentChange} />
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}><div style={{marginTop: '5px'}}><b>Category:</b></div></Col>
                <Col>
                    <TransactionTextWidget title={'Update Department'} placeholder={'Category'} value={category} maxRows={1}
                                           maxLength={64} onTextChange={onCategoryChange} />
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}>
                    <div style={labelStyle}><b>Vendor:</b></div>
                </Col>
                <Col>
                    <VendorWidget placeholder={'Vendor'} vendor={company} refresh={refresh} onApiFail={onApiFail} onVendorChange={onVendorIdChange} />
                </Col>
            </Row>
        </div>
    }

    return <div style={divStyle}>
        {contextHolder}
        <div style={{color: 'red'}}>
        <b>
            Caution: You are editing a Transaction.
        </b>
        </div>
        <Card title={<b>Transaction Details</b>}>
        {detailContent}
        </Card>
        {(log.length) > 0 &&
            <Card title={<b>Log</b>}>
                <TransactionLogTable log={log} />
            </Card>
        }
    </div>
}